import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { apiFetchResponse } from '@/lib/queryClient';

const API_ROOT = '/api/allocator/v1';
const schemaVersion = z.literal('allocator-api-v1');
const uuid = z.string().uuid();
const sha256 = z.string().regex(/^[0-9a-f]{64}$/);
const timestamp = z.string().min(1);
const nullableTimestamp = timestamp.nullable();

const errorEnvelopeSchema = z.object({
  schemaVersion,
  requestId: uuid,
  error: z.object({ code: z.string().min(1), message: z.string().min(1) }),
});

export class AllocatorApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'AllocatorApiError';
  }
}

async function responseError(response: Response): Promise<AllocatorApiError> {
  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    const parsed = errorEnvelopeSchema.safeParse(await response.json());
    if (parsed.success) {
      return new AllocatorApiError(
        response.status,
        parsed.data.error.code,
        parsed.data.error.message,
      );
    }
  }
  return new AllocatorApiError(
    response.status,
    response.status === 429 ? 'rate_limited' : 'invalid_error_response',
    response.statusText || 'Allocator API request failed',
  );
}

async function fetchSchema<S extends z.ZodType>(
  path: string,
  schema: S,
  signal?: AbortSignal,
): Promise<z.infer<S>> {
  const guarded = await apiFetchResponse('GET', `${API_ROOT}${path}`, undefined, signal);
  guarded.assertAuthorizationCurrent();
  if (!guarded.response.ok) throw await responseError(guarded.response);
  if (!(guarded.response.headers.get('content-type') ?? '').includes('application/json')) {
    throw new AllocatorApiError(
      guarded.response.status,
      'invalid_content_type',
      'Allocator API did not return JSON',
    );
  }
  const parsed = schema.safeParse(await guarded.response.json());
  guarded.assertAuthorizationCurrent();
  if (!parsed.success) {
    throw new AllocatorApiError(
      guarded.response.status,
      'invalid_response_contract',
      'Allocator API response did not match its versioned contract',
    );
  }
  return parsed.data;
}

const principalSchema = z.object({
  identityId: uuid,
  email: z.string().email(),
  name: z.string().min(1),
  role: z.string().min(1),
  organizationId: uuid,
  membershipId: uuid,
  engagementId: uuid,
  sessionEpoch: z.string().min(1),
  sessionExpiresAt: timestamp,
});

const meSchema = z.object({
  schemaVersion,
  requestId: uuid,
  principal: principalSchema,
  permissions: z.array(z.string().min(1)),
  grantExpiresAt: nullableTimestamp,
});

const accessStatusSchema = z.object({
  schemaVersion,
  requestId: uuid,
  state: z.enum(['active', 'no_active_grant']),
  permissions: z.array(z.string().min(1)),
  resourceCount: z.number().int().nonnegative(),
  checkedAt: timestamp,
});

const workspaceProfileSchema = z.object({
  id: uuid,
  allocator: z.string().min(1),
  program: z.string().min(1),
  accessMode: z.string().min(1),
  region: z.string().min(1),
  runtimeWindow: z.string().min(1),
  sourceSha256: sha256,
  createdAt: timestamp,
});

const accountSchema = z.object({
  observationId: uuid,
  accountId: uuid,
  accountKey: z.string().min(1),
  name: z.string().min(1),
  venue: z.string().min(1),
  environment: z.string().min(1),
  status: z.string().min(1),
  permission: z.string().min(1),
  capitalClass: z.string().min(1),
  capitalAmount: z.string().min(1),
  currency: z.string().min(1).nullable(),
  lastVerifiedAt: nullableTimestamp,
  observedAt: timestamp,
  sourceSha256: sha256,
  observationHash: sha256,
});

const mandateSchema = z.object({
  eventId: uuid,
  mandateId: uuid,
  eventSequence: z.number().int().positive(),
  seriesId: uuid.nullable(),
  candidateId: z.string().min(1),
  status: z.string().min(1),
  purpose: z.string().min(1),
  scopeSha256: sha256,
  authorityEvidenceSha256: sha256.nullable(),
  effectiveAt: timestamp,
  expiresAt: nullableTimestamp,
  previousEventHash: sha256.nullable(),
  eventHash: sha256,
});

const allocationSchema = z.object({
  eventId: uuid,
  allocationId: uuid,
  mandateId: uuid,
  eventSequence: z.number().int().positive(),
  seriesId: uuid,
  status: z.string().min(1),
  currency: z.string().min(1),
  requestedAmount: z.string().min(1),
  approvedAmount: z.string().min(1),
  fundedAmount: z.string().min(1),
  authorityEvidenceSha256: sha256.nullable(),
  effectiveAt: timestamp,
  previousEventHash: sha256.nullable(),
  eventHash: sha256,
});

const workspaceSchema = z.object({
  schemaVersion,
  requestId: uuid,
  organizationId: uuid,
  engagementId: uuid,
  workspace: workspaceProfileSchema,
  accounts: z.array(accountSchema),
  mandates: z.array(mandateSchema),
  allocations: z.array(allocationSchema),
});

const availableSeriesSchema = z.object({
  seriesId: uuid,
  evidenceState: z.literal('available'),
  candidateId: z.string().min(1),
  currency: z.string().min(1),
  returnBasis: z.string().min(1),
  evidenceClass: z.literal('live_capital'),
  t0At: timestamp,
  asOfSequence: z.number().int().positive(),
  projectionVersion: z.number().int().positive(),
  completenessStatus: z.string().min(1),
  finalizationStatus: z.string().min(1),
  sourceHeadSha256: sha256,
  correctionHeadSha256: sha256,
  generatedAt: timestamp,
  publishedAt: timestamp,
  publicationHash: sha256,
});

const unavailableSeriesSchema = z.object({
  seriesId: uuid,
  evidenceState: z.literal('unavailable'),
  reason: z.literal('formal_live_projection_not_published'),
});

const seriesSummarySchema = z.discriminatedUnion('evidenceState', [
  availableSeriesSchema,
  unavailableSeriesSchema,
]);

const seriesListSchema = z.object({
  schemaVersion,
  requestId: uuid,
  items: z.array(seriesSummarySchema),
  nextCursor: z.string().min(1).nullable(),
});

const seriesDetailSchema = z.object({
  schemaVersion,
  requestId: uuid,
  series: seriesSummarySchema,
});

const datasetEnvelope = {
  schemaVersion,
  requestId: uuid,
  evidenceClass: z.literal('live_capital'),
  seriesId: uuid,
  asOfSequence: z.number().int().positive(),
  finalizationStatus: z.string().min(1),
  completenessStatus: z.string().min(1),
  sourceHeadSha256: sha256,
  correctionHeadSha256: sha256,
  generatedAt: timestamp,
  publishedAt: timestamp,
};

function datasetSchema<S extends z.ZodType>(item: S) {
  return z.object({
    ...datasetEnvelope,
    evidenceState: z.enum(['available', 'summary_only', 'unavailable']),
    reason: z.string().min(1).optional(),
    items: z.array(item),
    nextCursor: z.string().min(1).nullable(),
  });
}

const returnItemSchema = z.object({
  closeDate: z.string().min(1),
  netReturn: z.string().min(1),
  cumulativeReturn: z.string().min(1),
  projectionCloseHash: sha256,
});

const exposureItemSchema = z.object({
  closeDate: z.string().min(1),
  grossExposure: z.string().min(1),
  netExposure: z.string().min(1),
  projectionCloseHash: sha256,
});

const capacityItemSchema = z.object({
  closeDate: z.string().min(1),
  liquidityLimitNotional: z.string().min(1),
  riskLimitNotional: z.string().min(1),
  estimatedCapacityNotional: z.string().min(1),
  utilizedNotional: z.string().min(1),
  headroomNotional: z.string().min(1),
  bindingConstraint: z.enum(['liquidity', 'risk', 'equal']),
  status: z.enum(['within_limit', 'exceeded']),
  sourceCapacitySha256: sha256,
  projectionCloseHash: sha256,
});

const pnlItemSchema = z.object({
  closeDate: z.string().min(1),
  grossRealizedPnl: z.string().min(1),
  changeInUnrealizedPnl: z.string().min(1),
  fundingPnl: z.string().min(1),
  projectionCloseHash: sha256,
});

const costItemSchema = z.object({
  closeDate: z.string().min(1),
  tradingFees: z.string().min(1),
  spreadCost: z.string().min(1),
  slippageCost: z.string().min(1),
  projectionCloseHash: sha256,
});

const reconciliationItemSchema = z.object({
  closeDate: z.string().min(1),
  status: z.string().min(1),
  sourceReconciliationSha256: sha256,
  sourceHeadSha256: sha256,
  projectionCloseHash: sha256,
});

const incidentItemSchema = z.object({
  closeDate: z.string().min(1),
  incidentCount: z.number().int().nonnegative(),
  detailState: z.literal('unavailable'),
  projectionCloseHash: sha256,
});

const lineageItemSchema = z.object({
  projectionVersion: z.number().int().positive(),
  previousPublicationHash: sha256.nullable(),
  publicationHash: sha256,
  payloadHash: sha256,
  sourceHeadSha256: sha256,
  correctionHeadSha256: sha256,
});

const reviewItemSchema = z.object({
  reviewScopeSha256: sha256,
  reviewerKeyId: z.string().min(1),
  reviewerPublicKeyHex: z.string().regex(/^[0-9a-f]{64}$/),
  reviewerSignatureHex: z.string().regex(/^[0-9a-f]{128}$/),
  reviewedAt: timestamp,
});

const executionSchema = datasetSchema(z.never());

const exportSummarySchema = z.object({
  exportId: uuid,
  publicationId: uuid,
  seriesId: uuid,
  exportSchemaVersion: z.literal('allocator-export-v1'),
  generatedAt: timestamp,
  fileCount: z.number().int().positive().max(16),
  totalByteCount: z.string().regex(/^[1-9][0-9]*$/),
  fileContractsSha256: sha256,
  manifestSha256: sha256,
  publisherKeyId: z.string().min(1),
  publisherPublicKeyHex: z.string().regex(/^[0-9a-f]{64}$/),
  publisherSignatureHex: z.string().regex(/^[0-9a-f]{128}$/),
  publishedAt: timestamp,
});

const exportListSchema = z.object({
  schemaVersion,
  requestId: uuid,
  evidenceClass: z.literal('live_capital'),
  seriesId: uuid,
  items: z.array(exportSummarySchema),
  nextCursor: z.string().min(1).nullable(),
});

const exportFileManifestSchema = z.object({
  fileName: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._-]{0,119}$/),
  contentType: z.enum([
    'application/json',
    'text/csv',
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ]),
  byteCount: z.string().regex(/^[1-9][0-9]*$/),
  contentSha256: sha256,
  fileContractSha256: sha256,
});

const exportManifestSchema = z.object({
  schemaVersion,
  requestId: uuid,
  evidenceClass: z.literal('live_capital'),
  seriesId: uuid,
  export: exportSummarySchema,
  files: z.array(exportFileManifestSchema).min(1).max(16),
});

export type AllocatorMe = z.infer<typeof meSchema>;
export type AllocatorAccessStatus = z.infer<typeof accessStatusSchema>;
export type AllocatorWorkspace = z.infer<typeof workspaceSchema>;
export type AllocatorSeriesSummary = z.infer<typeof seriesSummarySchema>;
export type AllocatorDataset = z.infer<ReturnType<typeof datasetSchema>>;
export type AllocatorExportSummary = z.infer<typeof exportSummarySchema>;
export type AllocatorExportManifest = z.infer<typeof exportManifestSchema>;

function query<S extends z.ZodType>(path: string, schema: S, enabled = true) {
  return useQuery<z.infer<S>, Error>({
    queryKey: [`${API_ROOT}${path}`],
    queryFn: ({ signal }) => fetchSchema(path, schema, signal),
    enabled,
    staleTime: 30_000,
  });
}

export function useAllocatorMe() {
  return query('/me', meSchema);
}

export function useAllocatorAccessStatus() {
  return query('/access-status', accessStatusSchema);
}

export function useAllocatorWorkspaceV1() {
  return query('/workspace', workspaceSchema);
}

export function useAllocatorSeries() {
  return query('/series?limit=100', seriesListSchema);
}

export function useAllocatorSeriesDetail(seriesId: string | undefined) {
  return query(`/series/${seriesId ?? 'disabled'}`, seriesDetailSchema, Boolean(seriesId));
}

function datasetQuery<S extends z.ZodType>(seriesId: string | undefined, name: string, schema: S) {
  return query(`/series/${seriesId ?? 'disabled'}/${name}?limit=100`, schema, Boolean(seriesId));
}

export function useAllocatorReturns(seriesId: string | undefined) {
  return datasetQuery(seriesId, 'returns', datasetSchema(returnItemSchema));
}

export function useAllocatorExposures(seriesId: string | undefined) {
  return datasetQuery(seriesId, 'exposures', datasetSchema(exposureItemSchema));
}

export function useAllocatorCapacity(seriesId: string | undefined) {
  return datasetQuery(seriesId, 'capacity', datasetSchema(capacityItemSchema));
}

export function useAllocatorPnl(seriesId: string | undefined) {
  return datasetQuery(seriesId, 'pnl', datasetSchema(pnlItemSchema));
}

export function useAllocatorCosts(seriesId: string | undefined) {
  return datasetQuery(seriesId, 'costs', datasetSchema(costItemSchema));
}

export function useAllocatorReconciliations(seriesId: string | undefined) {
  return datasetQuery(seriesId, 'reconciliations', datasetSchema(reconciliationItemSchema));
}

export function useAllocatorIncidents(seriesId: string | undefined) {
  return datasetQuery(seriesId, 'incidents', datasetSchema(incidentItemSchema));
}

export function useAllocatorLineage(seriesId: string | undefined) {
  return datasetQuery(seriesId, 'lineage', datasetSchema(lineageItemSchema));
}

export function useAllocatorReviews(seriesId: string | undefined) {
  return datasetQuery(seriesId, 'reviews', datasetSchema(reviewItemSchema));
}

export function useAllocatorExecutions(seriesId: string | undefined) {
  return datasetQuery(seriesId, 'executions', executionSchema);
}

export function useAllocatorExports(seriesId: string | undefined) {
  return query(
    `/series/${seriesId ?? 'disabled'}/exports?limit=100`,
    exportListSchema,
    Boolean(seriesId),
  );
}

export function useAllocatorExportManifest(
  seriesId: string | undefined,
  exportId: string | undefined,
) {
  return query(
    `/series/${seriesId ?? 'disabled'}/exports/${exportId ?? 'disabled'}/manifest`,
    exportManifestSchema,
    Boolean(seriesId && exportId),
  );
}

export async function downloadAllocatorExportFile(
  seriesId: string,
  exportId: string,
  fileName: string,
): Promise<void> {
  if (!uuid.safeParse(seriesId).success || !uuid.safeParse(exportId).success) {
    throw new AllocatorApiError(400, 'invalid_export_identity', 'Export identity is invalid');
  }
  if (!exportFileManifestSchema.shape.fileName.safeParse(fileName).success) {
    throw new AllocatorApiError(400, 'invalid_export_file', 'Export file name is invalid');
  }
  const guarded = await apiFetchResponse(
    'GET',
    `${API_ROOT}/series/${seriesId}/exports/${exportId}/files/${encodeURIComponent(fileName)}`,
  );
  guarded.assertAuthorizationCurrent();
  if (!guarded.response.ok) throw await responseError(guarded.response);
  const contentSha256 = guarded.response.headers.get('x-content-sha256');
  const responseExportId = guarded.response.headers.get('x-allocator-export-id');
  const contentDisposition = guarded.response.headers.get('content-disposition');
  const contentLength = guarded.response.headers.get('content-length');
  if (
    !sha256.safeParse(contentSha256).success ||
    responseExportId !== exportId ||
    contentDisposition !== `attachment; filename="${fileName}"` ||
    !contentLength ||
    !/^[1-9][0-9]*$/.test(contentLength)
  ) {
    throw new AllocatorApiError(503, 'invalid_export_headers', 'Export headers are invalid');
  }
  const blob = await guarded.response.blob();
  guarded.assertAuthorizationCurrent();
  if (blob.size !== Number(contentLength)) {
    throw new AllocatorApiError(503, 'invalid_export_length', 'Export length does not match');
  }
  const href = window.URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = href;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    window.URL.revokeObjectURL(href);
  }
}

export function firstAvailableSeries(
  items: readonly AllocatorSeriesSummary[] | undefined,
): Extract<AllocatorSeriesSummary, { evidenceState: 'available' }> | undefined {
  return items?.find(
    (item): item is Extract<AllocatorSeriesSummary, { evidenceState: 'available' }> =>
      item.evidenceState === 'available',
  );
}
